/**
 * WHO IS ON THIS CALL, live.
 *
 * The call page used to know exactly one thing about the other side: a single
 * `peerName` string captured when the call was placed. That is why a three-way
 * call's header named one person, and why the other party putting you on hold
 * changed nothing on your screen — the server has said `call.hold` since the
 * contract was written and no client had ever read it.
 *
 * The roster comes from `GET /calls/:id` and is re-read whenever the call's
 * realtime topic says it changed. It is a fetch, not a push-built cache: a
 * participant list assembled from events alone drifts the first time one frame
 * is missed, and a call is exactly where that must not happen.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ConversationId, UserId } from '@cyphex/types';
import type { CallParticipant } from '@cyphex/types/calls';
import { isApiRequestError } from '@/lib/api/errors';
import { getRealtimeClient } from '@/lib/realtime';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useTopicSubscription } from '@/features/realtime/useTopicSubscription';
import { callsApi } from '../data/callsApi';
import { CALL_KEYS } from '../data/queryKeys';
import { callPartiesView } from '../lib/callParties';

export interface CallParties {
  /** Everybody who is not me, in a STABLE order — see `byArrival`. */
  readonly counterparts: CallParticipant[];
  /** "Ganesh Kumar" · "Ganesh Kumar and 2 others" · "Call". */
  readonly label: string;
  /** Counterparts who have parked ME. Empty when nobody has. */
  readonly heldBy: CallParticipant[];
  /** Live people on the call, me included — what the header counts. */
  readonly liveCount: number;
  /** A counterpart is still being rung (a blind transfer in flight, say). */
  readonly ringing: boolean;
  /**
   * HAS ANYBODY ACTUALLY PICKED UP.
   *
   * Deliberately not `liveCount > 1` and deliberately not the media state: the
   * caller publishes into the empty room from the first second, so the media
   * says "connected" for the whole 45-second ring, and `liveCount` counts a
   * phone that is merely ringing. This is the answer to "is there a CONVERSATION
   * here", which is the only thing that makes "Move to meeting" mean anything —
   * the server draws the same line in `escalationBlocker`.
   *
   * FALSE WHILE THE ROSTER IS UNKNOWN, on purpose. `GET /calls/:id` failing
   * leaves every field here empty, and a client that cannot see who is on the
   * call must not offer a verb that ends it for everybody. The server refuses
   * the same case with a real message, so the cost of being wrong here is one
   * greyed menu item, not a stranded callee.
   *
   * A CONNECTED PHONE LINE DOES NOT COUNT, and that is not pedantry: a PSTN leg
   * has no browser to navigate, `counterparts` keeps it (`party.user?.id` is
   * `undefined`, which is not me), and the server's third refusal exists for
   * exactly that case. `canOfferEscalation` holds the rule and the argument.
   */
  readonly answered: boolean;
  /**
   * The chat thread the call was placed FROM, or null when it was placed from
   * the dialler or from history. Already on this payload — `GET /calls/:id`
   * returns the whole `Call` — so the chat rail costs no extra request.
   */
  readonly conversationId: ConversationId | null;
  /**
   * Everybody on the call except me, whatever state their leg is in.
   *
   * Deliberately NOT `counterparts`, which is filtered to live legs: resolving
   * a chat thread hashes this set, so a set that grows as people answer would
   * resolve to a different thread depending on who opened chat when.
   */
  readonly rosterUserIds: readonly UserId[];
  /**
   * `GET /calls/:id` gave up, so everything above is empty rather than true.
   *
   * The room itself degrades quietly — the header falls back to the handoff's
   * one name — but the chat rail cannot: resolving a thread needs the roster,
   * and without this flag its "still loading" and its "never arriving" are the
   * same skeleton for the rest of the call.
   */
  readonly isError: boolean;
  /** Ask again, by hand. Stable across renders. */
  readonly refetch: () => void;
}

export function useCallParties(callId: string): CallParties {
  const { user } = useAuth();
  const meId = user?.id ?? null;
  const queryClient = useQueryClient();
  const key = CALL_KEYS.detail(callId);

  useTopicSubscription([callId === '' ? null : `call:${callId}`]);

  const query = useQuery({
    queryKey: key,
    queryFn: () => callsApi.detail(callId),
    enabled: callId !== '',
    /* A roster that is one event stale is a roster that is wrong, and the
       refetch below is what keeps it honest — so nothing here is cached. */
    staleTime: 0,
    /*
     * A 4xx IS AN ANSWER, everything else is the absence of one.
     *
     * This route answers 403 for a call you were never part of and 404 for one
     * that has been purged; re-asking spends requests on a decision the server
     * has already made, which is why `retry: false` was here. But it covered
     * the other case too — a 502, a proxy hiccup, a socket dropped as the
     * laptop woke — and ONE unlucky request then left the room with no roster
     * and the chat rail on skeletons until the call ended. `ApiRequestError`
     * already draws exactly this line for the chat outbox; reuse it rather
     * than writing a second, subtly different one.
     */
    retry: (attempt, error) => attempt < 2 && (!isApiRequestError(error) || error.retryable),
  });

  /* Not `query.refetch`: that identity changes every render, and this is handed
     to a memoised child as the retry button's handler. */
  const refetch = useCallback(() => {
    void queryClient.refetchQueries({ queryKey: CALL_KEYS.detail(callId) });
  }, [queryClient, callId]);

  useEffect(() => {
    if (callId === '') return undefined;
    const realtime = getRealtimeClient();
    realtime.connect();
    return realtime.onEvent(({ event }) => {
      if (!('callId' in event) || event.callId !== callId) return;
      if (!event.type.startsWith('call.')) return;
      void queryClient.invalidateQueries({ queryKey: key });
    });
    // `key` is derived from callId and is stable for a given call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, queryClient]);

  return useMemo(
    () => ({
      /* EVERY DERIVED FACT IS `callPartiesView`'s, not this memo's. It used to
         be inlined here, where the only way to reach it was to render a
         component that needs a query client, a router and a live socket — so
         nothing ever did, and `answered: canOfferEscalation(live)` (my own leg,
         `connected` from the first second) passed every gate in the repo. */
      ...callPartiesView(query.data?.participants ?? [], meId),
      conversationId: query.data?.conversationId ?? null,
      isError: query.isError,
      refetch,
    }),
    [query.data, query.isError, meId, refetch],
  );
}
